// Implements: F-UIX-016, F-UIX-014, F-UIX-015, F-UIX-021, F-UIX-023, F-UIX-024, F-UIX-025, F-CNV-003, F-CNV-004, F-PRV-002, F-SHR-005, F-UIX-018
/**
 * `/lienzo/:mockupId` — the Lienzo editor (UX-05): the mockup with its conversation.
 *
 * The redesign mockup's editor. The topbar carries the way back to the gallery, the mockup's name
 * (edited in place), where it lives, its tags and its design profile as chips that open the
 * existing containers in popovers, "Diagrama" and "Detalles" (two labelled buttons that open the wide
 * right drawer on their tab; owner round 3) and "Nuevo" (back to the blank
 * Lienzo). The page itself is the split of `EditorWorkspace`: the conversation (the thread, the
 * turns and the composer, the ONLY way to generate, OD-6) and the preview of the current version
 * (F-PRV-002). The button in the conversation's head (or Shift+\) COLLAPSES the conversation to a
 * narrow rail (owner report, round 3): the rail holds the way back and says when a turn is running or
 * new messages arrived; Escape or Shift+\ expands it. The sidebar is not touched, and the collapse is
 * the editor's own state (never persisted). The drawer holds the diagram, the facts and the
 * version tree; "Compartir" sits right after the preview tabs (UX-06).
 *
 * An address that names no mockup the person can open says so and offers the ways out; nothing of
 * the editor is drawn for it. Every container is keyed by mockup (checklist 14): the editor itself
 * is, so a run, its typed text, the width and the drawer belong to ONE mockup.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { VersionCostFactContainer } from "../../modules/costs/containers/VersionCostFactContainer";
import { DiagramDrawerContainer } from "../../modules/diagrams/containers/DiagramDrawerContainer";
import { ConversationContainer } from "../../modules/conversation/containers/ConversationContainer";
import { AppliedProfileChipContainer } from "../../modules/design/containers/AppliedProfileChipContainer";
import { AppliedProfileIndicatorContainer } from "../../modules/design/containers/AppliedProfileIndicatorContainer";
import { MockupScopeChipContainer, MockupTagsChipContainer } from "../../modules/mockups/containers/MockupChipsContainer";
import { MockupDetailContainer } from "../../modules/mockups/containers/MockupDetailContainer";
import { MockupMoreActionsContainer } from "../../modules/mockups/containers/MockupMoreActionsContainer";
import { MockupNameContainer } from "../../modules/mockups/containers/MockupNameContainer";
import { ScopeChangeContainer } from "../../modules/mockups/containers/ScopeChangeContainer";
import { useMockupDetail } from "../../modules/mockups/hooks/useMockupDetail";
import { useMoreActionsBridge } from "../../modules/mockups/hooks/useMoreActionsBridge";
import { MockupPreviewContainer } from "../../modules/preview/containers/MockupPreviewContainer";
import { MockupAccessContainer } from "../../modules/sharing/containers/MockupAccessContainer";
import { ShareContainer } from "../../modules/sharing/containers/ShareContainer";
import type { ShareOpenRequest } from "../../modules/sharing/containers/ShareContainer";
import { UsageNoticeContainer } from "../../modules/usage/containers/UsageNoticeContainer";
import { VersionChipContainer } from "../../modules/versions/containers/VersionChipContainer";
import { VersionPreviewBannerContainer } from "../../modules/versions/containers/VersionPreviewBannerContainer";
import { VersionSideBySideContainer } from "../../modules/versions/containers/VersionSideBySideContainer";
import { VersionTreeContainer } from "../../modules/versions/containers/VersionTreeContainer";
import { useTranslation } from "../../shared/i18n";
import { useToast } from "../../shared/ui/molecules/ToastProvider";
import { Button, buttonClassName } from "../../shared/ui/atoms/Button";
import { Icon } from "../../shared/ui/atoms/Icon";
import { EmptyState } from "../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../shared/ui/molecules/ErrorState";
import { Tabs, tabPanelProps } from "../../shared/ui/molecules/Tabs";
import { Drawer } from "../../shared/ui/organisms/Drawer";
import { EditorWorkspace } from "../../shared/ui/organisms/EditorWorkspace";
import { useFocusModeKeys } from "../../shared/ui/useFocusModeKeys";
import { PageActions, PageTitle } from "../layout/Topbar";
import { ConversationRail, useRailActivity } from "./ConversationRail";
import styles from "./MockupPage.module.css";

type DrawerTab = "diagram" | "details";
const DRAWER_TABS = "editor-drawer";
const EXPAND_ID = "editor-conversation-expand";
const COLLAPSE_ID = "editor-conversation-collapse";
const VERSION_CHIP_ID = "editor-version-chip";

export function MockupPage(): ReactElement | null {
  const { mockupId } = useParams();
  if (mockupId === undefined) return null;
  return <Editor key={mockupId} mockupId={mockupId} />;
}

function Editor({ mockupId }: { readonly mockupId: string }): ReactElement {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const { push } = useToast();
  const mockup = useMockupDetail(mockupId);
  // Mockup v3 P4: the share popover's "Transferir" opens the dialog of "Más acciones".
  const moreActions = useMoreActionsBridge();
  const [collapsed, setCollapsed] = useState(false);
  const rail = useRailActivity(collapsed);
  const [drawer, setDrawer] = useState<DrawerTab | null>(null);
  // Mockup v3 P6: the diagram drawer's "Compartir" closes the drawer and opens the share popover on
  // the diagram of the version it shows; a new object per press, so a re-render reopens nothing.
  const [shareRequest, setShareRequest] = useState<ShareOpenRequest | null>(null);
  const shareDiagram = useCallback((versionId: string): void => {
    setDrawer(null);
    setShareRequest({ artifact: "diagram", versionId });
  }, []);
  // A version shown in the canvas instead of the current one (the version chip's preview).
  const [shownVersion, setShownVersion] = useState<string | null>(null);
  // A version compared side by side with the current one, in the preview column (owner round 3).
  const [comparing, setComparing] = useState<string | null>(null);
  const closeCompare = useCallback((): void => {
    setComparing(null);
    // The comparison unmounts; the focus goes back to where it was opened from.
    setTimeout(() => {
      document.getElementById(VERSION_CHIP_ID)?.focus();
    }, 0);
  }, []);
  const toggleCollapsed = useCallback((): void => {
    setCollapsed((value) => !value);
  }, []);
  // The gallery's "Compartir" lands here with `?compartir=1`: the share control reads it once to open
  // itself; the address then forgets it (a reload must not reopen), and where nothing can open, the
  // person is told why instead of seeing nothing happen.
  const wantsShare = params.get("compartir") === "1";
  const detail = mockup.data;
  useEffect(() => {
    if (!wantsShare || detail === undefined) return;
    if (!detail.may_share) push({ tone: "info", message: t("editor.share.notAllowed") });
    else if (detail.current_version_id === null) push({ tone: "info", message: t("editor.share.noVersion") });
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("compartir");
        return next;
      },
      { replace: true },
    );
  }, [wantsShare, detail, push, setParams, t]);
  // The collapsed conversation is hidden; the focus it held goes to the way back, never to the page,
  // and comes back to the collapse button when it expands. The first render moves nothing.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    document.getElementById(collapsed ? EXPAND_ID : COLLAPSE_ID)?.focus();
  }, [collapsed]);
  // While a comparison is open, its own Escape closes it first; the next one expands the conversation.
  useFocusModeKeys(collapsed && comparing === null, toggleCollapsed);

  const heading = (
    <h1 className={styles.srOnly} tabIndex={-1}>
      {mockup.data === undefined ? t("lienzo.title") : t("editor.title", { name: mockup.data.name })}
    </h1>
  );

  if (mockup.isError && mockup.error.status === 404) {
    return (
      <>
        {heading}
        <div className={styles.state}>
          <EmptyState
            icon="canvas"
            title={t("editor.notFound.title")}
            body={t("editor.notFound.body")}
            action={
              <>
                <Link className={buttonClassName("primary")} to="/lienzo">
                  {t("lienzo.newMockup")}
                </Link>
                <Link className={buttonClassName("secondary")} to="/">
                  {t("app.nav.gallery")}
                </Link>
              </>
            }
          />
        </div>
      </>
    );
  }
  if (mockup.isError) {
    return (
      <>
        {heading}
        <div className={styles.state}>
          <ErrorState
            error={mockup.error}
            onRetry={() => {
              void mockup.refetch();
            }}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {heading}
      <PageTitle>
        <nav className={styles.crumbs} aria-label={t("app.shell.breadcrumb")}>
          <Link className={styles.crumbLink} to="/lienzo">
            {t("app.nav.lienzo")}
          </Link>
          <span className={styles.crumbSeparator} aria-hidden="true" />
          <MockupNameContainer mockupId={mockupId} />
        </nav>
        <span className={styles.wideOnly}>
          <MockupScopeChipContainer mockupId={mockupId} />
          <MockupTagsChipContainer mockupId={mockupId} />
        </span>
        <span className={styles.mediumOnly}>
          <AppliedProfileChipContainer mockupId={mockupId} />
        </span>
        <VersionChipContainer
          mockupId={mockupId}
          triggerId={VERSION_CHIP_ID}
          previewing={shownVersion}
          onPreview={(versionId) => {
            setComparing(null);
            setShownVersion(versionId);
          }}
          onCompare={(versionId) => {
            setShownVersion(null);
            setComparing(versionId);
          }}
        />
      </PageTitle>
      <PageActions>
        <Button
          variant="ghost"
          size="sm"
          hasPopup="dialog"
          expanded={drawer === "diagram"}
          onClick={() => {
            setDrawer("diagram");
          }}
        >
          <Icon name="diagram" size="sm" />
          <span className={styles.actionLabel}>{t("editor.drawer.diagram")}</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          hasPopup="dialog"
          expanded={drawer === "details"}
          onClick={() => {
            setDrawer("details");
          }}
        >
          <Icon name="info" size="sm" />
          <span className={styles.actionLabel}>{t("editor.drawer.details")}</span>
        </Button>
        <Link className={buttonClassName("secondary")} to="/lienzo">
          <Icon name="plus" size="sm" />
          <span className={styles.actionLabel}>{t("lienzo.new")}</span>
        </Link>
        <MockupMoreActionsContainer key={mockupId} mockupId={mockupId} versionId={shownVersion} bridge={moreActions} />
      </PageActions>
      <EditorWorkspace
        collapsed={collapsed}
        rail={<ConversationRail expandId={EXPAND_ID} turnOpen={rail.turnOpen} unread={rail.unread} onExpand={toggleCollapsed} />}
        conversation={
          <ConversationContainer
            mockupId={mockupId}
            headActions={
              <Button
                id={COLLAPSE_ID}
                variant="ghost"
                iconOnly
                size="sm"
                label={t("editor.conversation.collapse")}
                keyShortcuts="Shift+\"
                onClick={toggleCollapsed}
              >
                <Icon name="panel-left-close" size="sm" />
              </Button>
            }
            statusNotice={(turnOpen) => <UsageNoticeContainer mockupId={mockupId} turnOpen={turnOpen} />}
            onActivity={rail.onActivity}
          />
        }
        preview={
          comparing !== null ? (
            <VersionSideBySideContainer key={comparing} mockupId={mockupId} versionId={comparing} onClose={closeCompare} />
          ) : (
          <MockupPreviewContainer
            mockupId={mockupId}
            showVersionId={shownVersion}
            share={
              <ShareContainer
                mockupId={mockupId}
                openOnMount={params.get("compartir") === "1"}
                openRequest={shareRequest}
                onTransfer={moreActions.openTransfer}
                peoplePanel={<MockupAccessContainer key={mockupId} mockupId={mockupId} onTransfer={moreActions.openTransfer} />}
              />
            }
            banner={
              shownVersion === null ? null : (
                <VersionPreviewBannerContainer
                  mockupId={mockupId}
                  versionId={shownVersion}
                  onBack={() => {
                    setShownVersion(null);
                  }}
                />
              )
            }
          />
          )
        }
      />
      <Drawer
        open={drawer !== null}
        title={t("editor.drawer.toggle")}
        size="wide"
        onClose={() => {
          setDrawer(null);
        }}
      >
        <Tabs
          label={t("editor.drawer.tabs")}
          idBase={DRAWER_TABS}
          items={[
            { id: "diagram", label: t("editor.drawer.diagram"), icon: "diagram" },
            { id: "details", label: t("editor.drawer.details"), icon: "info" },
          ]}
          selected={drawer ?? "diagram"}
          onSelect={(id) => {
            setDrawer(id === "details" ? "details" : "diagram");
          }}
        />
        <div className={styles.drawerPanel} {...tabPanelProps(DRAWER_TABS, drawer ?? "diagram")}>
          {drawer === "details" ? (
            <>
              <MockupDetailContainer mockupId={mockupId} section="facts" />
              <VersionCostFactContainer mockupId={mockupId} versionId={detail?.current_version_id} />
              <section className={styles.drawerSection} aria-labelledby="drawer-scope">
                <h3 id="drawer-scope" className={styles.drawerHeading}>
                  {t("scope.label")}
                </h3>
                <ScopeChangeContainer mockupId={mockupId} />
              </section>
              <AppliedProfileIndicatorContainer mockupId={mockupId} />
              <VersionTreeContainer mockupId={mockupId} />
            </>
          ) : (
            <DiagramDrawerContainer
              mockupId={mockupId}
              currentVersionId={detail?.current_version_id}
              onShare={shareDiagram}
            />
          )}
        </div>
      </Drawer>
    </>
  );
}

